import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import endpoints from "../../endpoint";
import axios from "axios";
import { Upload } from "tus-js-client";

// Async thunk for fetching strategies
export const fetchStrategies = createAsyncThunk(
  "strategy/fetchStrategies",
  async (_, { rejectWithValue }) => {
    const token = sessionStorage.getItem("token");
    try {
      const response = await axios.get(
        `${endpoints.serverBaseUrl}api/strategy`,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching strategies:", error);
      return rejectWithValue(
        error.response?.data || "An error occurred while fetching strategies."
      );
    }
  }
);

// async thunk for  fetching the course videos by coursID
export const addStrategy = createAsyncThunk(
  "strategy/addStrategy",
  async ({ strategyData, onSuccess }, { rejectWithValue }) => {
    const token = sessionStorage.getItem("token");
    console.log("startegy", strategyData.image);
    try {
      // **1. Upload Image**
      const imageUploadResponse = await axios.post(
        `${endpoints.serverBaseUrl}api/generate-presigned-url`,
        {
          fileName: strategyData?.image?.name,
          fileType: strategyData?.image.type,
        },
        { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
      );

      if (!imageUploadResponse.data.presignedUrl) {
        throw new Error("Failed to get presigned URL for image upload.");
      }

      const { presignedUrl: imagePresignedUrl, publicId: imageId } =
        imageUploadResponse.data;

      await axios.put(imagePresignedUrl, strategyData.image, {
        headers: { "Content-Type": strategyData?.image.type },
      });

      // **2. Upload Video**
      const videoUpload = new Upload(strategyData.video, {
        endpoint: `${endpoints.serverBaseUrl}api/generate-upload-url`,
        retryDelays: [0, 3000, 5000, 10000],
        chunkSize: 15 * 1024 * 1024, // 15MB chunks
        metadata: { name: strategyData.video.name },
        withCredentials: true,
        onError: (error) => {
          console.error("Upload Error:", error);
          alert("Video upload failed. Please try again.");
          return rejectWithValue("Video upload failed.");
        },

        onSuccess: async () => {
          console.log("Video upload successful!");
          const uploadURL = videoUpload.url;
          const videoId = uploadURL.split("/tus/")[1].split("?")[0];

          // **3. Prepare Strategy Payload**
          const strategyPayload = {
            videoId,
            title: strategyData.title,
            price: strategyData.price,
            imageId,
            description: strategyData.description,
            position: strategyData.position || "",
          };

          // **4. Send Strategy Data to Backend**
          try {
            const response = await axios.post(
              `${endpoints.serverBaseUrl}api/strategy`,
              strategyPayload,
              { headers: { Authorization: `Bearer ${token}` } }
            );

            if (onSuccess) onSuccess();

            return response.data;
          } catch (backendError) {
            console.error("Error creating strategy:", backendError);
            return rejectWithValue(
              backendError.response?.data || "Error creating the strategy"
            );
          }
        },

        onProgress: (bytesUploaded) => {
          const progress = (
            (bytesUploaded / strategyData.video.size) *
            100
          ).toFixed(2);
          console.log("Video upload progress:", progress);
        },
      });

      // Start the video upload process
      videoUpload.start();
    } catch (error) {
      console.error("Error adding strategy:", error);
      return rejectWithValue(
        error.response?.data || "An error occurred while adding the strategy."
      );
    }
  }
);

export const fetchPurchasedStrategies = createAsyncThunk(
  "/purchase/fetchPurchasedStrategies",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        endpoints.serverBaseUrl + "api/student/purchasedstrategy",
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );

      return response?.data?.purchasedStrategies;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  }
);

// Create an async thunk for fetching strategy by ID
export const fetchStrategyById = createAsyncThunk(
  "/strategy/fetchStrategyById",
  async (strategyId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${endpoints.serverBaseUrl}api/student/strategy/${strategyId}`,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );
      return response?.data?.strategy;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  }
);

export const updateStrategy = createAsyncThunk(
  "strategy/updateStrategy",
  async ({ strategyId, strategyData, onSuccess }, { rejectWithValue }) => {
    const token = sessionStorage.getItem("token");
    try {
      const updatePayload = {};

      // Delete existing image if a new one is uploaded
      if (strategyData.image) {
        const imageUploadResponse = await axios.post(
          `${endpoints.serverBaseUrl}api/generate-presigned-url`,
          {
            fileName: strategyData?.image?.name,
            fileType: strategyData?.image.type,
          },
          {
            withCredentials: true,
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!imageUploadResponse.data.presignedUrl) {
          throw new Error("Failed to get presigned URL for image upload.");
        }

        const { presignedUrl: imagePresignedUrl, publicId: imageId } =
          imageUploadResponse.data;

        await axios.put(imagePresignedUrl, strategyData.image, {
          headers: { "Content-Type": strategyData?.image.type },
        });

        updatePayload.imageId = imageId;
      }

      if (strategyData.video) {
        const uploadVideo = () =>
          new Promise((resolve, reject) => {
            const videoUpload = new Upload(strategyData.video, {
              endpoint: `${endpoints.serverBaseUrl}api/generate-upload-url`,
              retryDelays: [0, 3000, 5000, 10000],
              chunkSize: 15 * 1024 * 1024, // 15MB chunks
              metadata: { name: strategyData.video.name },
              withCredentials: true,
              onError: (error) => {
                console.error("Upload Error:", error);
                reject("Video upload failed.");
              },
              onSuccess: async () => {
                console.log("Video upload successful!");
                const uploadURL = videoUpload.url;
                const videoId = uploadURL.split("/tus/")[1].split("?")[0];
                resolve(videoId);
              },
              onProgress: (bytesUploaded) => {
                const progress = (
                  (bytesUploaded / strategyData.video.size) *
                  100
                ).toFixed(2);
                console.log("Video upload progress:", progress);
              },
            });
            videoUpload.start();
          });
        updatePayload.videoId = await uploadVideo();
      }

      // Only send data that's been modified
      if (strategyData.title) updatePayload.title = strategyData.title;
      if (strategyData.price) updatePayload.price = strategyData.price;
      if (strategyData.description)
        updatePayload.description = strategyData.description;
      if (strategyData.position) updatePayload.position = strategyData.position;

      // Send update request to the backend AFTER video upload is completed
      const response = await axios.put(
        `${endpoints.serverBaseUrl}api/strategy/${strategyId}`,
        updatePayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (onSuccess) onSuccess();

      return response.data;
    } catch (error) {
      console.error("Error updating strategy:", error);
      return rejectWithValue(
        error.response?.data || "Error updating the strategy"
      );
    }
  }
);

const StrategySlice = createSlice({
  name: "Strategy",
  initialState: {
    loading: false,
    error: null,
    strategy: null,
    purchasedStrategies: null,
    strategiesLoaded: false,
    purchasedStrategiesLoaded: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(addStrategy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addStrategy.fulfilled, (state, action) => {
        state.loading = false;
      })
      //fetch stragies
      .addCase(addStrategy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchStrategies.pending, (state) => {
        if (state.strategiesLoaded) return;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStrategies.fulfilled, (state, action) => {
        state.loading = false;
        state.strategy = action.payload;
        state.strategiesLoaded = true;
      })
      .addCase(fetchStrategies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      //purchesed course
      .addCase(fetchPurchasedStrategies.pending, (state) => {
        if (state.purchasedStrategiesLoaded) return;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchasedStrategies.fulfilled, (state, action) => {
        state.loading = false;
        state.purchasedStrategies = action.payload;
        state.purchasedStrategiesLoaded = true;
      })
      .addCase(fetchPurchasedStrategies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      //fetch strategy video by using id
      .addCase(fetchStrategyById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStrategyById.fulfilled, (state, action) => {
        state.loading = false;
        state.strategy = action.payload;
      })
      .addCase(fetchStrategyById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {} = StrategySlice.actions;
export default StrategySlice.reducer;
